import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Fab,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  LocalDining as ServingsIcon,
  Category as CategoryIcon,
  Lock as PrivateIcon,
  Share as ShareIcon,
  Archive as ArchiveIcon,
  LocalOffer as TagIcon,
} from '@mui/icons-material';
import { collection, query, orderBy, getDocs, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useProfile } from '../../contexts/ProfileContext';
import { useAuth } from '../../contexts/AuthContext';
import AddRecipeDialog from './AddRecipeDialog';
import RecipeDetailDialog from './RecipeDetailDialog';
import logger from '../../utils/logger';

const CATEGORIES = [
  'All',
  'Breakfast',
  'Lunch',
  'Dinner',
  'Appetizers',
  'Desserts',
  'Beverages',
  'Salads',
  'Soups',
  'Snacks',
  'Baking',
  'Other'
];

const RecipesPage = () => {
  const { profile } = useProfile();
  const { currentUser } = useAuth();

  // State
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [editRecipe, setEditRecipe] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const loadRecipes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!profile?.familyCode) {
        setError('No family code found.');
        return;
      }

      const recipesQuery = query(
        collection(db, 'families', profile.familyCode, 'recipes'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(recipesQuery);
      const recipesList = [];

      querySnapshot.forEach(doc => {
        recipesList.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setRecipes(recipesList);
    } catch (err) {
      logger.error('Error loading recipes:', err);
      setError('Failed to load recipes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [profile]);

  const applyFilters = useCallback(() => {
    let filtered = [...recipes];

    // Filter by visibility/privacy
    filtered = filtered.filter(recipe => {
      const visibility = recipe.visibility || 'family';

      // Family recipes - everyone can see
      if (visibility === 'family') return true;

      // Private recipes - only creator can see
      if (visibility === 'private') {
        return recipe.createdById === currentUser?.uid;
      }

      // Shared recipes - creator and shared users can see
      if (visibility === 'shared') {
        return recipe.createdById === currentUser?.uid ||
               (recipe.sharedWith || []).includes(currentUser?.uid);
      }

      return true;
    });

    // Filter archived recipes
    if (!showArchived) {
      filtered = filtered.filter(recipe => !recipe.isArchived);
    }

    // Search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(recipe =>
        recipe.title?.toLowerCase().includes(lowerSearch) ||
        recipe.description?.toLowerCase().includes(lowerSearch) ||
        recipe.ingredients?.some(ing => ing.toLowerCase().includes(lowerSearch))
      );
    }

    // Category filter
    if (selectedCategory && selectedCategory !== 'All') {
      filtered = filtered.filter(recipe => recipe.category === selectedCategory);
    }

    setFilteredRecipes(filtered);
  }, [recipes, searchTerm, selectedCategory, currentUser?.uid, showArchived]);

  // Load recipes
  useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  // Apply filters
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleViewRecipe = (recipe) => {
    setSelectedRecipe(recipe);
    setDetailDialogOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedRecipe(null);
  };

  const handleEditRecipe = (recipe) => {
    setEditRecipe(recipe);
    setDetailDialogOpen(false);
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (recipe) => {
    setRecipeToDelete(recipe);
    setDeleteDialogOpen(true);
    setDetailDialogOpen(false);
  };

  const handleArchiveRecipe = async (recipe) => {
    if (!recipe || !profile?.familyCode) return;

    try {
      const newArchivedState = !recipe.isArchived;
      await updateDoc(doc(db, 'families', profile.familyCode, 'recipes', recipe.id), {
        isArchived: newArchivedState,
        archivedAt: newArchivedState ? serverTimestamp() : null,
        archivedBy: newArchivedState ? currentUser.uid : null
      });
      setDetailDialogOpen(false);
      loadRecipes();
    } catch (err) {
      logger.error('Error archiving recipe:', err);
      setError('Failed to archive recipe. Please try again.');
    }
  };

  // Count archived recipes for the toggle label
  const archivedCount = recipes.filter(r => r.isArchived &&
    (r.visibility === 'family' || r.createdById === currentUser?.uid || (r.sharedWith || []).includes(currentUser?.uid))
  ).length;

  const handleDeleteConfirm = async () => {
    if (!recipeToDelete || !profile?.familyCode) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'families', profile.familyCode, 'recipes', recipeToDelete.id));
      setDeleteDialogOpen(false);
      setRecipeToDelete(null);
      loadRecipes();
    } catch (err) {
      logger.error('Error deleting recipe:', err);
      setError('Failed to delete recipe. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setEditRecipe(null);
  };

  const handleRecipeAdded = () => {
    loadRecipes();
    setEditRecipe(null);
  };

  // Get visibility icon for recipe card
  const getVisibilityIcon = (recipe) => {
    const visibility = recipe.visibility || 'family';
    switch (visibility) {
      case 'private':
        return (
          <Tooltip title="Private - Only you can see this">
            <PrivateIcon sx={{ fontSize: 14, color: 'warning.main' }} />
          </Tooltip>
        );
      case 'shared':
        return (
          <Tooltip title={`Shared with ${(recipe.sharedWith || []).length} people`}>
            <ShareIcon sx={{ fontSize: 14, color: 'info.main' }} />
          </Tooltip>
        );
      default:
        return null; // Don't show icon for family recipes (default)
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
              Family Recipes
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Preserve and share your family's favorite recipes
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {archivedCount > 0 && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    size="small"
                  />
                }
                label={`Show archived (${archivedCount})`}
              />
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Add Recipe
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filter */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search recipes by name, ingredient, or description..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={handleCategoryChange}
              >
                {CATEGORIES.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {filteredRecipes.length} of {recipes.length} recipes
      </Typography>

      {/* Recipe Grid */}
      {filteredRecipes.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm || selectedCategory !== 'All'
              ? 'No recipes found matching your search'
              : 'No recipes yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm || selectedCategory !== 'All'
              ? 'Try adjusting your filters'
              : 'Be the first to share a family recipe!'}
          </Typography>
          {(!searchTerm && selectedCategory === 'All') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Add Recipe
            </Button>
          )}
        </Card>
      ) : (
        <Grid container spacing={3}>
          {filteredRecipes.map(recipe => (
            <Grid item xs={12} sm={6} md={4} key={recipe.id}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                opacity: recipe.isArchived ? 0.7 : 1,
                borderLeft: recipe.isArchived ? '4px solid' : 'none',
                borderColor: 'warning.main'
              }}>
                {recipe.photoURL && (
                  <CardMedia
                    component="img"
                    height="200"
                    image={recipe.photoURL}
                    alt={recipe.title}
                  />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                        {recipe.title}
                      </Typography>
                      {recipe.isArchived && (
                        <Chip
                          icon={<ArchiveIcon />}
                          label="Archived"
                          size="small"
                          color="warning"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                    {getVisibilityIcon(recipe)}
                  </Box>

                  {recipe.category && (
                    <Chip
                      icon={<CategoryIcon />}
                      label={recipe.category}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {recipe.description?.length > 100
                      ? `${recipe.description.substring(0, 100)}...`
                      : recipe.description}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {recipe.prepTime && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TimeIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        <Typography variant="caption">{recipe.prepTime} min</Typography>
                      </Box>
                    )}
                    {recipe.servings && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ServingsIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        <Typography variant="caption">{recipe.servings} servings</Typography>
                      </Box>
                    )}
                  </Box>

                  {recipe.createdBy && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <PersonIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.disabled">
                        by {recipe.createdBy}
                      </Typography>
                    </Box>
                  )}

                  {/* Tagged Members indicator */}
                  {recipe.taggedMembers && recipe.taggedMembers.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <TagIcon sx={{ fontSize: 14, color: 'info.main' }} />
                      <Typography variant="caption" color="info.main">
                        {recipe.taggedMembers.length} tagged
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => handleViewRecipe(recipe)}
                  >
                    View Recipe
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Recipe FAB — only on mobile where header button may be off-screen */}
      <Fab
        color="primary"
        aria-label="add recipe"
        sx={{ position: 'fixed', bottom: 160, right: 16, display: { sm: 'none' } }}
        onClick={() => setAddDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* Add/Edit Recipe Dialog */}
      <AddRecipeDialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        onRecipeAdded={handleRecipeAdded}
        editRecipe={editRecipe}
      />

      {/* Recipe Detail Dialog */}
      <RecipeDetailDialog
        open={detailDialogOpen}
        onClose={handleCloseDetail}
        recipe={selectedRecipe}
        onEdit={handleEditRecipe}
        onDelete={handleDeleteClick}
        onArchive={handleArchiveRecipe}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Recipe</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{recipeToDelete?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RecipesPage;
